import { useState, useEffect, useRef } from "react";
import { isBrowser } from "react-device-detect";
import { isMobile } from "react-device-detect";

import * as Common from "../../commons/common";
import * as TypeUtils from "../../commons/typeUtils";

import ReactApexChart from "react-apexcharts";
import moment from "moment";

// ApexCharts 는 group 으로 묶인 차트 중 하나의 옵션이 바뀌면(react-apexcharts 의 updateOptions)
// 그 옵션을 chart.id 까지 포함해 그룹 전체 w.config 에 덮어쓴다. 이후 각 차트가 unmount 되며
// destroy() 가 "자기 config.chart.id" 로 전역 레지스트리(window.Apex._chartInstances)를 지우므로
// 첫 차트 외에는 죽은 인스턴스(globals.tooltip === null)가 레지스트리에 남는다.
// 같은 group 의 차트에 다시 마우스를 올리면 툴팁 동기화 루프가 죽은 인스턴스의
// globals.tooltip.ttItems 에서 TypeError 를 내고 중단되어 모든 차트의 툴팁이 사라진다
// (운행이력 상세 페이지 재진입 시 툴팁 미표시 버그). 살아있지 않은 인스턴스를 정리한다.
// group 차트에서 ApexCharts 가 툴팁 글자를 써 넣는 행 참조(globals.tooltip.ttItems)가
// 그 차트의 실제 툴팁 DOM 행과 달라지는 경우가 있다(그룹 동기화 중 차트 재생성). 그러면 hover 시
// 행은 표시되지만 글자가 다른 노드에 쓰여 "빈 툴팁 상자"가 된다. 자기 DOM 행으로 다시 맞춘다.
function resyncApexTooltipItems(w: any) {
    const tooltip = w && w.globals && w.globals.tooltip;
    const baseEl = w && w.globals && w.globals.dom && w.globals.dom.baseEl;
    if (!tooltip || !baseEl || !Array.isArray(tooltip.ttItems)) return;
    const rows = baseEl.querySelectorAll(".apexcharts-tooltip-series-group");
    if (rows.length === 0 || rows.length !== tooltip.ttItems.length) return;
    const stale = tooltip.ttItems.some((node: any) => !node || !baseEl.contains(node));
    if (stale) {
        tooltip.ttItems = Array.prototype.slice.call(rows);
    }
}

// ApexCharts 는 툴팁을 데이터 포인트 옆에 두고 아래로 넘치면 위로 밀어 올리기만 하므로(Position.moveTooltip),
// 항목이 많은 툴팁은 top 이 음수가 되어 차트 위로 벗어난다. 위치 계산 뒤(rAF) top 을 0 이상으로 보정한다.
function clampTooltipTop(w: any) {
    const baseEl = w && w.globals && w.globals.dom && w.globals.dom.baseEl;
    if (!baseEl) return;
    window.requestAnimationFrame(() => {
        const tooltipEl = baseEl.querySelector(".apexcharts-tooltip") as HTMLElement | null;
        if (!tooltipEl || !tooltipEl.classList.contains("apexcharts-active")) return;
        const top = parseFloat(tooltipEl.style.top || "0");
        if (!isNaN(top) && top < 0) {
            tooltipEl.style.top = "0px";
        }
    });
}

function purgeStaleApexChartInstances() {
    const apex = (window as any).Apex;
    if (!apex || !Array.isArray(apex._chartInstances)) return;
    apex._chartInstances = apex._chartInstances.filter((item: any) => {
        const chart = item && item.chart;
        const alive = chart && chart.w && chart.w.globals && chart.w.globals.tooltip;
        const attached = chart && chart.el && document.body.contains(chart.el);
        return Boolean(alive && attached);
    });
}

function CanDataChart({
    series,
    option,
    onClickChart,
    onMountedChart,
    onZoomed,
}: {
    series: TypeUtils.ChartData[];
    option: Record<string, any>;
    onClickChart?: (dataPointIndex: number) => void;
    onMountedChart?: () => void;
    onZoomed?: (lastZoom: any) => void;
}) {
    const [chartOption, setChartOption] = useState<Record<string, any> | undefined>();
    const [lastZoom, setLastZoom] = useState(null);
    const chartRef = useRef(null);

    useEffect(() => {
        purgeStaleApexChartInstances();
        return () => {
            // react-apexcharts 의 componentWillUnmount(destroy) 가 끝난 뒤에 정리한다.
            setTimeout(purgeStaleApexChartInstances, 0);
        };
    }, []);

    useEffect(() => {
        if (!option) return;

        let zoomed = false;

        const chart_option = {
            ...option,
            chart: {
                ...option.chart,
                toolbar: {
                    show: true,
                    tools: {
                        download: false,
                        selection: false,
                        pan: false,
                        zoomin: !isBrowser,
                        zoomout: !isBrowser,
                    },
                },
                zoom: {
                    enabled: true,
                    type: "x",
                },
                animations: {
                    enabled: false,
                    easing: "linear",
                    dynamicAnimation: {
                        speed: 500,
                    },
                },
                events: {
                    // group 차트는 한 차트의 updateOptions 가 그룹 전체 w.config 에 복사되며 chart.id 까지
                    // 덮어쓴다(UpdateHelpers._updateOptions). 그대로 두면 이후 어떤 차트가 unmount 될 때
                    // destroy() 가 "덮어써진 id" 로 다른 차트의 전역 등록을 지워, 그 차트는 hover 해도
                    // 자기 툴팁만 안 나오고(그룹 동기화 대상에서 빠짐) 죽은 인스턴스만 남는다.
                    // 매 update 직후 자기 id 를 복원한다.
                    updated: function (chartContext: any, w: any) {
                        if (w && w.config && w.config.chart && w.globals && w.globals.chartID) {
                            w.config.chart.id = w.globals.chartID;
                        }
                        resyncApexTooltipItems(w);
                    },
                    mounted: function (chartContext: any, w: any) {
                        resyncApexTooltipItems(w);
                    },
                    mouseMove: function (event: any, chartContext: any, config: any) {
                        resyncApexTooltipItems(chartContext && chartContext.w);
                        clampTooltipTop(chartContext && chartContext.w);
                    },
                    // mounted: function (chartContext: any, config: any) {
                    //     console.log("mounted", chartContext, config);
                    //     onMountedChart && onMountedChart();
                    // },
                    // updated: function (chartContext: any, config: any) {
                    //     console.log("updated", chartContext, config);
                    // },
                    // beforeZoom: function (chartContext: any, { xaxis }: any) {
                    //     // console.log("beforeZoom", xaxis);
                    //     if (xaxis.min !== undefined && xaxis.max !== undefined) {
                    //         zoomed = true;
                    //     }
                    //     setLastZoom({ min: xaxis.min, max: xaxis.max });

                    //     return {
                    //         xaxis: {},
                    //     };
                    // },
                    zoomed: function (chartContext: any, { xaxis, yaxis }: any) {
                        // console.log("zoomed", xaxis);

                        // if (xaxis.hasOwnProperty("min") && xaxis.min === undefined && xaxis.hasOwnProperty("max") && xaxis.max === undefined) {
                        //     setLastZoom({ min: xaxis.min, max: xaxis.max });
                        // }

                        if (xaxis.min !== undefined && xaxis.max !== undefined) {
                            zoomed = true;
                        }
                        // setLastZoom({ min: xaxis.min, max: xaxis.max });
                    },
                    click: function (event: any, chartContext: any, config: any) {
                        if (zoomed) {
                            zoomed = false;
                            return;
                        }
                        if (config.seriesIndex === -1 || config.dataPointIndex === -1) return;
                        if (!isMobile && onClickChart !== undefined) onClickChart(config.dataPointIndex);
                    },
                },
            },
            dataLabels: {
                enabled: false,
            },
            title: {
                align: "center",
            },
            xaxis: {
                type: "number",
                tickAmount: 30,
                labels: {
                    formatter: function (val: number, index: number) {
                        return moment(val).format(Common.FORMAT_TIME);
                    },
                },
                tooltip: {
                    formatter: function (val: number, index: number) {
                        return moment(val).format(Common.FORMAT_DATE_TIME);
                    },
                },
                ...option.xaxis,
            },
            yaxis: {
                labels: {
                    formatter: function (val: number, index: number) {
                        return val.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    },
                },
                ...option.yaxis,
            },
            tooltip: {
                ...option.tooltip,
                enabled: true,
                shared: true,
                // apexcharts 3.45 기본값 true: 포맷된 값이 parseFloat 로 0 이면 그 행(항목명+값)을 숨긴다.
                // 속도 0, CAN 미수신(0) 차트가 "빈 툴팁 상자"로 보이던 원인. 0 도 값이므로 표시한다.
                hideEmptySeries: false,
                // 항목별 시리즈 색상 점을 표시해 어느 선이 어느 값인지 알 수 있게 한다.
                marker: {
                    show: true,
                },
                x: {
                    show: false,
                },
            },
            legend: {
                showForSingleSeries: true,
                fontFamily: "Pretendard-Regular",
                fontSize: "14px",
            },
            noData: {
                text: "No Data",
                align: "center",
                verticalAlign: "middle",
                offsetX: 0,
                offsetY: 0,
                style: {
                    color: "#8b0000",
                    fontSize: "32px",
                    fontFamily: "Pretendard-Bold",
                },
            },
            plotOptions: {
                line: {
                    curve: "smooth",
                },
                bar: {
                    horizontal: false,
                    columnWidth: "10%",
                },
            },
            stroke: {
                width: 2,
            },
            colors: ["#0A84FF", "#5E5CE6", "#BF5AF2", "#FF2D55", "#FF453A", "#FF9F0A", "#FFD60A", "#32D74B", "#64D2FF"],

            axisBorder: {
                show: true,
                color: "#a9abb3",
                height: 1,
                width: "100%",
            },
            markers: {
                size: 0,
            },
        };

        setChartOption(chart_option);

        // if (chartRef.current && lastZoom) {
        //     setTimeout(() => {
        //         chartRef.current.chart.zoomX(lastZoom.min, lastZoom.max);
        //         zoomed = false;
        //     }, 50); // 차트 업데이트 후 줌 상태 복원
        // }
    }, [option]);

    // useEffect(() => {
    //     onZoomed && onZoomed(lastZoom);
    // }, [lastZoom]);

    return (
        <>
            {chartOption && (
                <ReactApexChart
                    // 브라우저는 width 100% 라 가로 스크롤이 필요 없다. overflow hidden 이면 항목이 많은 툴팁(7~9행)이
                    // 차트 위쪽으로 넘칠 때 첫 행이 잘리므로 visible 로 둔다. 모바일(2500px 고정폭)만 가로 스크롤 유지.
                    style={{
                        backgroundColor: "white",
                        overflowX: isBrowser ? "visible" : "auto",
                        overflowY: isBrowser ? "visible" : "hidden",
                        position: "relative",
                    }}
                    options={chartOption}
                    series={series}
                    type={chartOption.chart.type}
                    width={isBrowser ? "100%" : "2500px"}
                    height={chartOption.chart.height}
                    // ref={chartRef}
                />
            )}
        </>
    );
}

export default CanDataChart;
